// useSocketEvent — типизированная подписка на Socket.IO event с auto-cleanup.
//
// Использует ServerToClientEvents из server-build — handler получает payload
// с правильным типом без any/unknown cast'ов.
//
// Паттерн:
//   useSocketEvent("note:ready", (payload) => {
//     // payload: { noteId: string; materialId: string | null; jobId?: string }
//     void utils.notes.list.invalidate();
//   });
//
// `useSocketEvent` сам обрабатывает случай socket=null (cold start, logout) —
// просто не подписывается. Когда socket подключится, эффект перезапустится
// через изменение зависимости.

import type { ServerToClientEvents } from "@server/socket-events";
import { useEffect, useRef } from "react";
import { useSocket } from "@/providers/socket-provider";

type EventName = keyof ServerToClientEvents;

/**
 * Извлекает тип payload из ServerToClientEvents[E].
 * Все события сервера — `(payload: T) => void`, поэтому берём первый параметр.
 */
type EventPayload<E extends EventName> = Parameters<ServerToClientEvents[E]>[0];

/**
 * Подписывается на Socket.IO event с типизированным payload.
 *
 * Handler в ref: стабильность ссылки НЕ нужна, effect НЕ переподписывается
 * на каждый рендер. Иначе off→on между кадрами даёт окно, в котором
 * terminal-событие (`note:ready` и т.п.) теряется → UI залипает на прелоадере.
 */
export function useSocketEvent<E extends EventName>(
	event: E,
	handler: (payload: EventPayload<E>) => void,
): void {
	const socket = useSocket();
	const handlerRef = useRef(handler);
	handlerRef.current = handler;
	useEffect(() => {
		if (!socket) return;
		const listener = (payload: EventPayload<E>): void => {
			handlerRef.current(payload);
		};
		socket.on(event, listener as never);
		return () => {
			socket.off(event, listener as never);
		};
	}, [socket, event]);
}
